import { IDB_workflowsReadAll, IDB_workflowRead } from '../IDB/IDB.table.workflow'
import { type Workflow, isFlowWorkflow } from '../../../app-core/lib/workflow/Workflow'
import type { Procedure } from '../../../app-core/lib/procedure/Procedure'
import { I18N_replaceNameSlugs, I18N_replaceNameSlug } from '../I18N/I18N.slugs'
// Array.prototype.flat is not available in the version of ecmascript we target
import { flatten, sortBy } from 'lodash'
import { Proc_fetchAll } from '../Proc/Proc.fetch'
import { filterOutDeleted } from '../../../shared/lib/functional/deletable.no-deps'
import { identity } from '../../../shared/lib/functional/function.no-deps'
import { IDB_oldWorkflows } from '../IDB/IDB.dexie'
import { WF_ensureVersionOnDisk } from './WF.WorkflowReference'
import { isOnline } from '../Serv/Serv.socketClient'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import {
    type AppFlowWorkflow,
    type WorkflowReference,
    type WorkflowVersionIdentifier,
    isAppFlowWorkflow,
} from '../../../shared/domain/workflows/types.no-deps'

export async function WF_fetch(logger: ClientLogger, workflowId: number, opts: { includeDeleted: boolean }) {
    try {
        const workflow = await IDB_workflowRead(workflowId)
        if (workflow == null) {
            logger.warn(`cannot find workflow`, { workflowId })
            return null
        }

        if (!opts.includeDeleted && workflow.isDeleted) {
            logger.warn(`not returning deleted workflow`, { workflowId })
            return null
        }

        return I18N_replaceNameSlug(workflow)
    } catch (error) {
        logger.error(error)
        return null
    }
}

export async function WF_fetchByIds(logger: ClientLogger, workflowIds: number[], includeDeleted: boolean = true) {
    return Promise.all(workflowIds.map((id) => WF_fetch(logger, id, { includeDeleted })))
}

export async function WF_fetchAll(includeDeleted: boolean = false) {
    return IDB_workflowsReadAll()
        .then(includeDeleted ? identity : filterOutDeleted)
        .then(I18N_replaceNameSlugs)
        .then((workflows) => sortBy(workflows, 'name'))
}

function getParentProcedures(allProcedures: Procedure[], workflow: Workflow): Procedure[] {
    return allProcedures.filter((proc) => proc.activityTemplates.includes(workflow.id))
}

export function WF_getProcedurePairs(
    allWorkflows: Workflow[],
    allProcedures: Procedure[]
): Array<{ workflow: Workflow; procedure: Procedure }> {
    const pairs = allWorkflows.map((workflow) =>
        getParentProcedures(allProcedures, workflow).map((procedure) => ({ workflow, procedure }))
    )
    return flatten(pairs)
}

export async function WF_fetchProcedurePairs() {
    const allWorkflows = await WF_fetchAll()
    const allProcedures = await Proc_fetchAll()
    return WF_getProcedurePairs(allWorkflows, allProcedures)
}

export async function WF_resolveReference(
    logger: ClientLogger,
    reference: WorkflowReference
): Promise<AppFlowWorkflow> {
    const workflowId = reference.id

    if (isAppFlowWorkflow(reference)) {
        logger.warn(`can't resolve reference for inlined workflow`, { workflowId })
        return reference
    }

    const workflowVersion = reference.version

    const latest = await WF_fetch(logger, reference.id, { includeDeleted: true })
    if (latest == null) {
        logger.warn(`failed to find workflow in workflows table`, { workflowId, workflowVersion })
        return fetchMissingWorkflowFromServer(logger, reference)
    }

    if (latest.numberOfVersions === reference.version) {
        logger.log(`found the right workflow version in workflows table`, { workflowId })
        return isFlowWorkflow(latest) && latest
    }

    const oldVersion = await IDB_oldWorkflows().get({ id: reference.id, numberOfVersions: reference.version })
    if (oldVersion == null) {
        logger.warn(`failed to find workflow in old workflows table`, { workflowId, workflowVersion })
        return fetchMissingWorkflowFromServer(logger, reference)
    }

    logger.log(`found the right workflow version in old workflows table`, { workflowId })
    return isFlowWorkflow(oldVersion) && I18N_replaceNameSlug(oldVersion)
}

async function fetchMissingWorkflowFromServer(logger: ClientLogger, reference: WorkflowVersionIdentifier) {
    // If not online
    if (!(await isOnline())) {
        // Throw because we can't do anything
        throw logger.error(`cannot find workflow`, { workflowId: reference.id, workflowVersion: reference.version })
    }

    // Get the missing version from the server
    const fetchedVersion = await WF_ensureVersionOnDisk(logger, reference)

    if (fetchedVersion == null) {
        throw logger.error('failed to find missing workflow version', {
            workflowId: reference.id,
            workflowVersion: reference.version,
        })
    } else {
        logger.log('found missing workflow version', { workflowId: reference.id, workflowVersion: reference.version })
        return isFlowWorkflow(fetchedVersion) && I18N_replaceNameSlug(fetchedVersion)
    }
}
